import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import { Lara2Sizes } from './primeng-lara-sizes';

//A filled button severity with white text: its colour, then darker on hover and when pressed
function severity(colour: string, hover: string, active: string) {
  return {
    background: colour,
    hoverBackground: hover,
    activeBackground: active,
    borderColor: colour,
    hoverBorderColor: hover,
    activeBorderColor: active,
    color: '#ffffff',
    hoverColor: '#ffffff',
    activeColor: '#ffffff'
  };
}

/*
  The PrimeNG theme. Since PrimeNG 18 the theme is a TypeScript preset instead of a CSS file.

  Lara, as the app has always used, at its PrimeNG 18-21 sizes (primeng-lara-sizes.ts), brought back in
  line with the old lara-light-purple CSS theme:
  - the primary colour is violet rather than Lara's default emerald (the old #8B5CF6 is violet 500)
  - the greys are Tailwind gray rather than Lara 18's bluish slate
  - a selected toggle or select button option is filled with the primary colour, where Lara 18
    leaves it white
*/
export const WannabookPreset = definePreset(Lara, Lara2Sizes, {
  semantic: {
    primary: {
      50: '{violet.50}',
      100: '{violet.100}',
      200: '{violet.200}',
      300: '{violet.300}',
      400: '{violet.400}',
      500: '{violet.500}',
      600: '{violet.600}',
      700: '{violet.700}',
      800: '{violet.800}',
      900: '{violet.900}',
      950: '{violet.950}'
    },
    //Form fields (inputs, dropdowns, date pickers...) as tall as the old theme made them - Lara 18
    //made them shorter, so they no longer lined up with the buttons beside them
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.75rem',
      sm: {
        fontSize: '0.875rem',
        paddingX: '0.65625rem',
        paddingY: '0.65625rem'
      },
      lg: {
        fontSize: '1.25rem',
        paddingX: '0.9375rem',
        paddingY: '0.9375rem'
      }
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '{gray.50}',
          100: '{gray.100}',
          200: '{gray.200}',
          300: '{gray.300}',
          400: '{gray.400}',
          500: '{gray.500}',
          600: '{gray.600}',
          700: '{gray.700}',
          800: '{gray.800}',
          900: '{gray.900}',
          950: '{gray.950}'
        }
      }
    }
  },
  components: {
    //Buttons as the old theme drew them - its padding, bold labels and severity colours. Lara 18
    //pads them less, uses a lighter label and turned secondary into a pale grey button.
    button: {
      root: {
        borderRadius: '6px',
        //No flex gap - our templates put their own icon spans in buttons with a margin-right, and
        //the gap doubled it. PrimeNG's own button icons get their margin from theme-legacy.css.
        gap: '0',
        paddingX: '1.25rem',
        paddingY: '0.75rem',
        label: {
          fontWeight: '700'
        },
        sm: {
          fontSize: '0.875rem',
          paddingX: '1.09375rem',
          paddingY: '0.65625rem'
        }
      },
      colorScheme: {
        light: {
          root: {
            secondary: severity('#64748b', '#475569', '#334155'),
            info: severity('#0ea5e9', '#0284c7', '#0369a1'),
            success: severity('#22c55e', '#16a34a', '#15803d'),
            warn: severity('#f97316', '#ea580c', '#c2410c'),
            help: severity('#a855f7', '#9333ea', '#7e22ce'),
            danger: severity('#ef4444', '#dc2626', '#b91c1c')
          }
        }
      }
    },
    //Accordion tabs as separate rounded panels, as the old theme drew them - the gap between panels
    //and the open header's square bottom corners are in theme-legacy.css
    accordion: {
      header: {
        padding: '1.25rem',
        borderWidth: '1px',
        borderRadius: '6px',
        color: '{surface.500}',
        hoverColor: '{surface.700}',
        activeColor: '{surface.700}',
        first: {
          borderWidth: '1px',
          topBorderRadius: '6px'
        },
        last: {
          bottomBorderRadius: '6px',
          activeBottomBorderRadius: '0'
        }
      },
      content: {
        padding: '1.25rem',
        color: '{surface.600}'
      }
    },
    //The calendar button a filled primary button, as it was, rather than Lara 18's pale grey one
    datepicker: {
      dropdown: {
        width: '3rem',
        borderColor: '{primary.color}',
        hoverBorderColor: '{primary.hover.color}',
        activeBorderColor: '{primary.active.color}'
      },
      colorScheme: {
        light: {
          dropdown: {
            background: '{primary.color}',
            hoverBackground: '{primary.hover.color}',
            activeBackground: '{primary.active.color}',
            color: '{primary.contrast.color}',
            hoverColor: '{primary.contrast.color}',
            activeColor: '{primary.contrast.color}'
          }
        }
      }
    },
    togglebutton: {
      root: {
        checkedBackground: '{primary.color}',
        checkedColor: '{primary.contrast.color}',
        checkedBorderColor: '{primary.color}'
      },
      icon: {
        checkedColor: '{primary.contrast.color}'
      }
    }
  }
});

export const primeNgThemeOptions = {
  preset: WannabookPreset,
  options: {
    //The app has only ever had a light theme - without this it turns dark when the OS is in dark mode
    darkModeSelector: false,
    //Into the primeng layer, which layers.css puts above bootstrap - so the theme still beats
    //Bootstrap's reboot, and our own unlayered CSS still beats the theme
    cssLayer: {
      name: 'primeng',
      order: 'bootstrap, primeng'
    }
  }
};
